import {
    Component,
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    Type,
    ViewContainerRef,
} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {UiWidgetFormFields} from '../data/ui-widget-form-fields';
import {MvsFormFieldDto} from '../../../../../dto/form/dto/mvs-form-field.dto';
import {MvsFormDto} from '../../../../../dto/form/dto/mvs-form.dto';
import {WidgetData} from '../../../../widget-data';
import {Subscription} from 'rxjs';
import {
    MvsFormFieldOutputConfigBaseComponent
} from '../../../../../form/field/output/mvs-form-field-output-config-base.component';

@Component({
    selector: 'config-component-wrapper',
    template: '',
    standalone: false
})
export class ConfigComponentWrapper implements OnInit, OnDestroy, OnChanges {
  @Input() component: Type<any>;
  @Input() formGroup: FormGroup;
  @Input() formControlName: string;
  @Input() formFields: MvsFormFieldDto[];
  @Input() form: MvsFormDto;
  @Input() widgetUiField: UiWidgetFormFields;
  @Input() widgetData: WidgetData;
  @Input() row: UiWidgetFormFields;
  @Output() uiConfigDataChange = new EventEmitter<{
    row: UiWidgetFormFields;
    uiConfigData: any;
  }>();

  private componentRef: ComponentRef<any>;
  private formSub?: Subscription;

  initialized: boolean;

  constructor(
    private vcRef: ViewContainerRef,
    private resolver: ComponentFactoryResolver
  ) {}

  ngOnInit() {
    this.ensureFormGroup();
    this.refreshComponent();
    this.initialized = true;
  }

  private ensureFormGroup() {

    if (!this.formControlName && !this.formGroup) {
      console.warn('ConfigComponentWrapper: formControlName was not provided.');
      return;
    }

    if (!this.formGroup) {
      this.formGroup = new FormGroup({
        [this.formControlName]: new FormControl(''),
        uiConfigData: new FormGroup({})
      });
    }
    else {
      // formGroup exists → ensure the needed control exists
      if (!this.formGroup.contains(this.formControlName)) {
        this.formGroup.addControl(this.formControlName, new FormControl(''));
      }
    }
  }

  refreshComponent() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }

    const factory: ComponentFactory<MvsFormFieldOutputConfigBaseComponent> = this.resolver.resolveComponentFactory(this.component);
    this.componentRef = this.vcRef.createComponent(factory);

    const instance = this.componentRef.instance;

    instance.formGroup = this.formGroup;
    instance.form = this.form;
    instance.formFields = this.getAllFormFields(this.form);
    instance.widgetUiField = this.formGroup as any;
    instance.row = this.row;

    // CONTROLLED reactive sync
    this.formSub?.unsubscribe();
    this.formSub = this.formGroup.valueChanges.subscribe(value => {
      if (!value?.uiConfigData) return;

      // 1 update row immediately
      this.widgetUiField.uiConfigData = value.uiConfigData;

      this.row.uiConfigData = value.uiConfigData;

      // 2 emit to parent
      this.uiConfigDataChange.emit({
        row: this.widgetUiField,
        uiConfigData: value.uiConfigData
      });
    });
  }

  getAllFormFields(form: MvsFormDto, parentPath: string = ''): any[] {
    let fields: any[] = [];

    // Extract fields from the current form
    if (form.formFields) {
      Object.entries(form.formFields).forEach(([key, field]) => {
        fields.push({
          ...field,
          absoluteId: parentPath ? `${parentPath}#${field.id}` : field.id
        });
      });
    }

    // Recursively extract fields from subforms
    if (form.subForms) {
      Object.entries(form.subForms).forEach(([subFormKey, subForm]) => {
        fields.push(...this.getAllFormFields(subForm, parentPath ? `${parentPath}#${subFormKey}` : subFormKey));
      });
    }

    return fields;
  }

  ngOnDestroy() {

    this.formSub?.unsubscribe();
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formGroup'] && this.initialized) {
      this.ensureFormGroup();
    }
    if (this.initialized) {
      this.refreshComponent();
    }
  }

}
